import { NavLink } from "react-router-dom"
import allfcLogo from "@/assets/logos/allfc.svg"
import Icon from "@/components/common/Icon"

interface PublicMenuItem {
  to: string
  label: string
}

interface PublicMobileMenuProps {
  items: readonly PublicMenuItem[]
  isOpen: boolean
  onClose: () => void
}

/**
 * 모바일 메뉴 서랍.
 *
 * 메뉴를 한 줄에 늘어놓으면 이름이 접히거나 가로 스크롤로 숨는다 —
 * 관리자 사이드바와 같은 방식으로 햄버거 안에 세로로 넣는다 —
 * 여는 버튼이 헤더 오른쪽이라 서랍도 오른쪽에서 나온다.
 * 관리자 로그인도 여기에 들어간다(모바일 헤더에는 로고와 햄버거만 남긴다).
 */
function menuLinkClass({ isActive }: { isActive: boolean }): string {
  return `focus-ring app-txt-body1 flex items-center justify-between px-3 py-3 font-bold whitespace-nowrap transition ${
    isActive
      ? "bg-app-primary-500 text-app-text-100"
      : "text-app-text-300 hover:bg-app-secondary-300 hover:text-app-text-400"
  }`
}

export default function PublicMobileMenu({
  items,
  isOpen,
  onClose,
}: Readonly<PublicMobileMenuProps>) {
  function onClickDim() {
    onClose()
  }

  function onClickMenuItem() {
    onClose()
  }

  function onClickClose() {
    onClose()
  }

  return (
    <>
      {isOpen && (
        <button
          type="button"
          onClick={onClickDim}
          aria-label="메뉴 닫기"
          className="fixed inset-0 z-30 bg-[var(--app-color-dim)] lg:hidden"
        />
      )}

      <aside
        className={`fixed inset-y-0 right-0 z-40 flex w-72 max-w-[85vw] flex-col border-l border-app-secondary-500 bg-app-secondary-100 transition-transform duration-300 lg:hidden ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex h-20 shrink-0 items-center justify-between gap-3 border-b border-app-secondary-500 px-5">
          <img
            src={allfcLogo}
            alt="ALLFC"
            className="h-10 w-auto"
          />
          <button
            type="button"
            onClick={onClickClose}
            aria-label="메뉴 닫기"
            className="focus-ring cursor-pointer p-2 text-app-text-300 transition hover:bg-app-secondary-300 hover:text-app-text-400"
          >
            <Icon name="close" />
          </button>
        </div>

        <nav
          aria-label="주요 메뉴 (모바일)"
          className="flex-1 overflow-y-auto px-4 py-6"
        >
          <ul className="flex list-none flex-col gap-1">
            {items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={onClickMenuItem}
                  className={menuLinkClass}
                >
                  {item.label}
                  <Icon
                    name="arrow"
                    size={18}
                  />
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-app-secondary-500 p-4">
          <NavLink
            to="/login"
            onClick={onClickMenuItem}
            className="focus-ring app-btn app-btn-outline app-btn-block"
          >
            관리자 로그인
          </NavLink>
        </div>
      </aside>
    </>
  )
}
